import { Link } from 'react-router-dom';
import { Phone, Mail, Clock, MapPin, ArrowUpRight } from 'lucide-react';
import { useFetch } from '../../hooks/useFetch';
import { settingsApi } from '../../api/contentApi';
import { pagesApi } from '../../api/pagesApi';
import { reopenCookieConsent } from '../../hooks/useCookieConsent';
import { Button } from '../ui/Button';
import { FacebookIcon, InstagramIcon, LinkedinIcon, TiktokIcon } from '../ui/SocialIcons';
import { SiteLogo } from '../ui/SiteLogo';
import './Footer.css';

const API_URL = process.env.REACT_APP_API_URL;

const COLONNES = [
  {
    titre: 'Particuliers',
    liens: [
      { to: '/particuliers', label: 'Services à la personne' },
      { to: '/particuliers/faq', label: 'FAQ' },
      { to: '/devis', label: 'Demander un devis' },
    ],
  },
  {
    titre: 'Professionnels',
    liens: [
      { to: '/professionnels', label: 'Solutions professionnelles' },
      { to: '/particuliers/faq', label: 'FAQ' },
      { to: '/devis', label: 'Demander un devis' },
    ],
  },
  {
    titre: 'Entreprise',
    liens: [
      { to: '/a-propos', label: 'À propos' },
      { to: '/blog', label: 'Actualités & réalisations' },
      { to: '/contact', label: 'Contact' },
    ],
  },
];

const LIENS_LEGAUX = [
  { to: '/mentions-legales', label: 'Mentions légales' },
  { to: '/cgu', label: 'CGU' },
  { to: '/confidentialite', label: 'Politique de confidentialité' },
  { to: '/cookies', label: 'Cookies' },
  { to: '/plan-du-site', label: 'Plan du site' },
];

const SLUGS_FIXES = new Set(
  [...COLONNES.flatMap((c) => c.liens), ...LIENS_LEGAUX].map((l) => l.to.replace(/^\//, ''))
);

export function Footer() {
  const { data: settings } = useFetch(() => settingsApi.public(), []);
  const { data: pages } = useFetch(() => pagesApi.list(), []);

  // Une page CMS déjà couverte par un lien fixe (À propos, mentions légales...) est
  // exclue de l'injection dynamique, pour ne jamais créer de doublon dans le footer.
  const liensPagesFooter = (pages || [])
    .filter((p) => p.afficher_footer && !SLUGS_FIXES.has(p.slug))
    .map((p) => ({ to: `/${p.slug}`, label: p.menu_label || p.titre }));

  const colonnes = COLONNES.map((colonne) =>
    colonne.titre === 'Entreprise' ? { ...colonne, liens: [...colonne.liens, ...liensPagesFooter] } : colonne
  );

  const reseaux = [
    { icon: FacebookIcon, url: settings?.['reseaux.facebook'], label: 'Facebook' },
    { icon: InstagramIcon, url: settings?.['reseaux.instagram'], label: 'Instagram' },
    { icon: LinkedinIcon, url: settings?.['reseaux.linkedin'], label: 'LinkedIn' },
    { icon: TiktokIcon, url: settings?.['reseaux.tiktok'], label: 'TikTok' },
  ].filter((r) => r.url);

  const labels = [
    { url: settings?.['footer.logo_fedesap_url'], alt: 'FEDESAP' },
    { url: settings?.['footer.logo_sap_url'], alt: 'Services à la Personne' },
  ].filter((l) => l.url);

  const coordonnees = [
    { icon: Phone, label: settings?.['contact.telephone'] },
    { icon: Mail, label: settings?.['contact.email'] },
    { icon: Clock, label: settings?.['contact.horaires'] },
    { icon: MapPin, label: settings?.['contact.adresse'] },
  ].filter((c) => c.label);

  return (
    <footer className="site-footer">
      <div className="container site-footer__top">
        <div className="site-footer__brand">
          <p className="site-footer__logo">
            <SiteLogo tone="sombre" height={40} />
          </p>
          <p className="site-footer__tagline">
            Ménage, jardinage, bricolage et nettoyage professionnel — un interlocuteur unique, deux
            expertises.
          </p>

          <Link to="/devis">
            <Button>Demander un devis</Button>
          </Link>

          {reseaux.length > 0 && (
            <div className="site-footer__social">
              {reseaux.map((r) => (
                <a key={r.label} href={r.url} target="_blank" rel="noreferrer" aria-label={r.label}>
                  <r.icon size={18} />
                </a>
              ))}
            </div>
          )}

          {labels.length > 0 && (
            <div className="site-footer__labels">
              {labels.map((l) => (
                <img key={l.alt} src={`${API_URL}${l.url}`} alt={l.alt} className="site-footer__label-logo" />
              ))}
            </div>
          )}
        </div>

        {colonnes.map((colonne) => (
          <div className="site-footer__col" key={colonne.titre}>
            <h3>{colonne.titre}</h3>
            <nav>
              {colonne.liens.map((lien) => (
                <Link key={lien.label} to={lien.to}>
                  {lien.label}
                </Link>
              ))}
            </nav>
          </div>
        ))}

        {coordonnees.length > 0 && (
          <div className="site-footer__col">
            <h3>Coordonnées</h3>
            <div className="site-footer__coordonnees">
              {coordonnees.map((c) => (
                <p key={c.label}>
                  <c.icon size={15} />
                  <span>{c.label}</span>
                </p>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="site-footer__bottom">
        <div className="container site-footer__bottom-inner">
          <p>© {new Date().getFullYear()} AXEO Services. Tous droits réservés.</p>
          <nav className="site-footer__legal">
            {LIENS_LEGAUX.map((lien) => (
              <Link key={lien.to} to={lien.to}>
                {lien.label}
              </Link>
            ))}
            {settings?.['cookies.actif'] !== '0' && (
              <button type="button" className="site-footer__top-link" onClick={reopenCookieConsent}>
                Gérer les cookies
              </button>
            )}
          </nav>
          <button
            type="button"
            className="site-footer__top-link"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            Haut de page <ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    </footer>
  );
}
